import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * Move-video modal. Two axes of movement:
 *   1. Which trade sections (ProBound / ProEpoxy / ProDecor) the video
 *      appears in — a video can live in multiple.
 *   2. Which category within a section it's filed under — a video sits
 *      in exactly one category at a time (backend model constraint).
 *
 * The category picker only shows categories that belong to at least one
 * of the currently-selected sections, so the operator can't file a video
 * into a category that would then be invisible from every section it's in.
 */
export default function VideoMoveModal({
  open,
  movingVideo,
  allSections,
  moveSections,
  toggleMoveSection,
  categories = [],
  moveCategoryId,
  setMoveCategoryId,
  colors,
  onClose,
  onSave,
}) {
  const eligibleCategories = useMemo(
    () => (categories || []).filter((c) => moveSections.includes(c.section)),
    [categories, moveSections],
  );

  if (!open || !movingVideo) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-800">Move Video</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          <strong>{movingVideo.title}</strong>
        </p>

        {/* Section (trade) picker */}
        <p className="text-xs font-bold uppercase text-gray-500 mb-2">Trade sections</p>
        <p className="text-xs text-gray-500 mb-2">Pick every trade this video should appear under.</p>
        <div className="flex flex-wrap gap-2 mb-5">
          {allSections.map((s) => (
            <button
              key={s.id}
              type="button"
              data-testid={`move-section-${s.id}`}
              onClick={() => toggleMoveSection(s.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors border-2 ${
                moveSections.includes(s.id)
                  ? 'text-white border-transparent'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
              }`}
              style={moveSections.includes(s.id) ? { backgroundColor: s.color } : {}}
            >
              {s.name}
            </button>
          ))}
        </div>

        {/* Category picker — only shown when we actually have any */}
        {setMoveCategoryId && (
          <>
            <p className="text-xs font-bold uppercase text-gray-500 mb-2">Category</p>
            <p className="text-xs text-gray-500 mb-2">Which category in the selected trade should the video sit under?</p>
            <select
              value={moveCategoryId || ''}
              onChange={(e) => setMoveCategoryId(e.target.value)}
              className="w-full border-2 rounded-lg px-3 py-2 text-sm font-medium bg-white mb-6"
              data-testid="move-category-select"
              disabled={eligibleCategories.length === 0}
            >
              <option value="">— Leave current category —</option>
              {eligibleCategories.map((c) => {
                const s = allSections.find((s) => s.id === c.section);
                return (
                  <option key={c.id} value={c.id}>
                    {s ? `${s.name} · ` : ''}{c.name}
                  </option>
                );
              })}
            </select>
            {eligibleCategories.length === 0 && (
              <p className="text-xs text-amber-600 -mt-4 mb-6">
                No categories exist for the selected trade(s) yet. Create one first, then move the video.
              </p>
            )}
          </>
        )}

        <div className="flex gap-3">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Cancel
          </Button>
          <Button
            data-testid="save-move-btn"
            onClick={onSave}
            disabled={moveSections.length === 0}
            style={{ backgroundColor: colors.primary }}
            className="flex-1 text-white"
          >
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}
